import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";

/**
 * EmailComposeDialog — reusable "send email" modal. Pass any of
 * customerId/jobId/invoiceId/estimateId to scope the email to a thread
 * (and resolve merge fields in picked templates).
 *
 *   <EmailComposeDialog
 *     open={emailOpen}
 *     onClose={() => setEmailOpen(false)}
 *     customerId={customer.id}
 *     prefillTo={customer.email}
 *     prefillSubject="..."
 *     prefillBody="..."
 *   />
 */
export default function EmailComposeDialog({
  open,
  onClose,
  customerId = null,
  jobId = null,
  invoiceId = null,
  estimateId = null,
  prefillTo = "",
  prefillSubject = "",
  prefillBody = "",
  onSent,
}) {
  const [to, setTo] = useState(prefillTo || "");
  const [subject, setSubject] = useState(prefillSubject || "");
  const [body, setBody] = useState(prefillBody || "");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTo(prefillTo || "");
      setSubject(prefillSubject || "");
      setBody(prefillBody || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    if (!to.trim()) { toast.error("Recipient email required"); return; }
    if (!subject.trim()) { toast.error("Subject required"); return; }
    if (!body.trim()) { toast.error("Message body required"); return; }
    setBusy(true);
    try {
      const payload = {
        to_email: to.trim(),
        subject: subject.trim(),
        body,
      };
      if (customerId) payload.customer_id = customerId;
      if (jobId) payload.job_id = jobId;
      if (invoiceId) payload.invoice_id = invoiceId;
      if (estimateId) payload.estimate_id = estimateId;
      const { data } = await api.post("/emails/send", payload);
      toast.success("Email sent");
      if (onSent) onSent(data);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#FF5A00]"/>
            Send email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>To</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              data-testid="email-compose-to"
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="email-compose-subject"
              />
            </div>
            <TemplatePicker
              channel="email"
              customerId={customerId}
              jobId={jobId}
              invoiceId={invoiceId}
              estimateId={estimateId}
              onPick={({ subject: s, body: b }) => {
                if (s) setSubject(s);
                if (b) setBody(b);
              }}
              testIdPrefix="email-compose-template-picker"
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              ref={bodyRef}
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="font-mono-accent text-sm"
              data-testid="email-compose-body"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Plain-text input, auto-wrapped into HTML on send. Line breaks and blank lines are preserved.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="email-compose-send">
            <Send className="w-4 h-4 mr-1"/> {busy ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
